import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./contexts/ThemeContext";

// Public pages — Landing kept eager (LCP target). Others lazy-loaded.
import Landing from "./pages/Landing";

const JobsListing = lazy(() => import("./pages/JobsListing"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const ApplicationForm = lazy(() => import("./pages/ApplicationForm"));
const Confirmation = lazy(() => import("./pages/Confirmation"));
const TrackApplication = lazy(() => import("./pages/TrackApplication"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const PrivacyDeclined = lazy(() => import("./pages/PrivacyDeclined"));
const CompletionForm = lazy(() => import("./pages/CompletionForm"));

// Admin pages — never on the critical path for public users.
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminJobs = lazy(() => import("./pages/AdminJobs"));
const AdminApplications = lazy(() => import("./pages/AdminApplications"));
const AdminJobApplications = lazy(() => import("./pages/AdminJobApplications"));
const AdminAnalysis = lazy(() => import("./pages/AdminAnalysis"));
const AdminFormTemplates = lazy(() => import("./pages/AdminFormTemplates"));
const AdminToolSalaryReject = lazy(() => import("./pages/AdminToolSalaryReject"));
const AdminToolAgenda = lazy(() => import("./pages/AdminToolAgenda"));
const AdminToolMatch = lazy(() => import("./pages/AdminToolMatch"));
const AdminToolBlacklist = lazy(() => import("./pages/AdminToolBlacklist"));
const AdminTools = lazy(() => import("./pages/AdminTools"));

// Minimalist skeleton shown while a route chunk is being fetched. Stays in
// brand color so there's no white-flash on theme-aware pages.
const RouteFallback = () => (
  <div
    style={{
      minHeight: "100vh",
      background: "var(--bg-primary)",
    }}
    aria-hidden="true"
  />
);

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <div className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/vagas" element={<JobsListing />} />
                <Route path="/vagas/:jobId" element={<JobDetail />} />
                <Route path="/aplicar/:jobId" element={<ApplicationForm />} />
                <Route path="/confirmacao/:trackingCode" element={<Confirmation />} />
                <Route path="/acompanhar" element={<TrackApplication />} />
                <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
                <Route path="/politica-recusada" element={<PrivacyDeclined />} />
                <Route path="/completar/:token" element={<CompletionForm />} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/vagas" element={<AdminJobs />} />
                <Route path="/admin/candidaturas" element={<AdminApplications />} />
                <Route path="/admin/ferramentas" element={<AdminTools />} />
                <Route path="/admin/ferramentas/reprovar-salario" element={<AdminToolSalaryReject />} />
                <Route path="/admin/ferramentas/agenda" element={<AdminToolAgenda />} />
                <Route path="/admin/ferramentas/match" element={<AdminToolMatch />} />
                <Route path="/admin/ferramentas/blacklist" element={<AdminToolBlacklist />} />
                <Route path="/admin/candidaturas/:jobId" element={<AdminJobApplications />} />
                <Route path="/admin/candidaturas/:jobId/analise" element={<AdminAnalysis />} />
                <Route path="/admin/formularios" element={<AdminFormTemplates />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
